/*
 *  -----------------------------------------------------------------------
 *    <copyright file="admin-organisation-roles.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useLazyQuery } from '@apollo/client/react';
import { useAuthorization, useBoolean, useTranslation } from 'hooks';
import {
    OrganisationMemberFields,
    OrganisationRoleFields,
    UserAndRoleFields_User_role_OrganisationRole_rights_OrganisationRoleRight as OrganisationRoleRightFields,
} from 'queries/generated/fragments.generated';
import { GET_ORGANISATION_MEMBERS_AND_ROLES } from 'queries/uam';
import NotificationService from 'utils/notification';
import AddOrganizationRole from 'views/components/admin/add-organization-role';
import AdminRolesGrid from 'views/components/admin/organisations/admin-roles-grid';
import Button from 'views/components/core/buttons/button';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import Error from 'views/components/error/error';

import 'views/App.scss';

const AdminOrganisationRoles = ({ roleId }: { roleId: string }) => {
    const { t } = useTranslation();
    const user = useAuthorization();

    const canEdit = user?.canWriteSystemAdminLevel(true);
    const navigate = useNavigate();
    const showAddRole = useBoolean(false);
    const [currentRole, setCurrentRole] = useState(null);
    const [roles, setRoles] = useState([]);
    const { organisationId } = user;
    // Use GET_ORGANISATION_MEMBERS_AND_ROLES here to get the data
    const [loadRoles, { data, error, loading }] = useLazyQuery(GET_ORGANISATION_MEMBERS_AND_ROLES);

    const calculateMembersCount = (id: string, members: OrganisationMemberFields[]) =>
        members.filter((member: OrganisationMemberFields) => member.roleId === id)?.length;

    const formationRoles = () => {
        let { roles, members } = data.organisation;
        roles = roles.map((role: OrganisationRoleFields) => ({
            ...role,
            description: role.description || '',
            usersAmount: calculateMembersCount(role.id, members),
            isActiveRAM:
                !!role.rights.find(
                    (el: OrganisationRoleRightFields) =>
                        el.moduleId === 2 && el.permissions > 0 && el.element?.match(/page/),
                ) === true
                    ? 'Active'
                    : '',
            isActiveCM:
                !!role.rights.find(
                    (el: OrganisationRoleRightFields) =>
                        el.moduleId === 1 && el.permissions > 0 && el.element?.match(/page/),
                ) === true
                    ? 'Active'
                    : '',
            isActiveCM2:
                !!role.rights.find(
                    (el: OrganisationRoleRightFields) =>
                        el.moduleId === 8 && el.permissions > 0 && el.element?.match(/page/),
                ) === true
                    ? 'Active'
                    : '',
            isActiveSM:
                !!role.rights.find(
                    (el: OrganisationRoleRightFields) =>
                        el.moduleId === 16 && el.permissions > 0 && el.element?.match(/page/),
                ) === true
                    ? 'Active'
                    : '',
            isActiveDQ:
                !!role.rights.find(
                    (el: OrganisationRoleRightFields) =>
                        el.moduleId === 32 && el.permissions > 0 && el.element?.match(/page/),
                ) === true
                    ? 'Active'
                    : '',
        }));
        return roles;
    };

    useEffect(() => {
        if (data?.organisation) setRoles(formationRoles());
    }, [data]);

    useEffect(() => {
        loadRoles();
    }, [organisationId]);

    if (!user || !organisationId) return null;

    if (loading) return <Loading float />;

    if (error) return <Error error={error} />;

    const onAddOrganizationRoleClose = (action: string) => {
        if (action === 'error') {
            NotificationService.showNotification(t('errorMessage'));
        } else if (action === 'has-users') {
            NotificationService.showNotification(t('admin:roleContainUsersNotification'));
        } else {
            if (action === 'add') {
                NotificationService.showNotification(t('admin:roleSuccessCreated'));
            } else if (action === 'update') {
                NotificationService.showNotification(t('admin:roleSuccessUpdated', { id: currentRole.recordId }));
            }
            showAddRole.setFalse();
        }
    };

    return (
        <>
            <PageTitle text={t('uam:manageRoles')} />
            <Button
                text="uam:addRole"
                onClick={() => {
                    setCurrentRole(null);
                    showAddRole.setTrue();
                }}
                className="main-button"
                disabled={!canEdit}
                style={{ marginLeft: 'auto', display: 'flex', marginTop: 20, marginBottom: 20 }}
            />
            <AddOrganizationRole
                roles={roles}
                organisationId={organisationId}
                currentRole={currentRole}
                open={showAddRole.value}
                onClose={onAddOrganizationRoleClose}
            />
            <AdminRolesGrid
                roles={roles}
                showPager
                addScroll
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: false,
                    useFilterChips: false,
                }}
                rowActions={(role: OrganisationRoleFields) => [
                    {
                        label: 'admin:editRole',
                        icon: ['fal', 'edit'],
                        enabled: canEdit,
                        onClick: () => {
                            setCurrentRole(role);
                            showAddRole.setTrue();
                        },
                    },
                    {
                        label: 'admin:editPermissions',
                        enabled: true,
                        icon: ['fal', 'edit'],
                        onClick: () => {
                            navigate(`/organisation/permissions/${role.id}`);
                        },
                    },
                ]}
            />
        </>
    );
};

export default AdminOrganisationRoles;
